import { useState, useEffect, useCallback } from "react"
import { getUserPhoto, putUserPhoto } from "../apiLib/apiLib"
import { useAppContext } from "../contextLib/contextLib"
import { deleteStickyValue, retrieveStickyValue } from "./hooksLib"

/**
 * Custom hook for managing recruiter photos with migration from localStorage to API
 *
 * This hook handles the transition from localStorage-based photo storage to API-based storage.
 * It will automatically upload existing localStorage photos to the API and clean up localStorage on success.
 *
 * @returns [photo, setPhoto, isLoading] - stateful getter, setter, and loading state for the recruiter photo
 */
export function useRecruiterPhoto(): [string | undefined, (photo: string | undefined) => Promise<void>, boolean] {
	const [photo, setPhotoState] = useState<string | undefined>(undefined)
	const [isLoading, setIsLoading] = useState(false)
	const [hasCheckedLocalStorage, setHasCheckedLocalStorage] = useState(false)

	const { user, company } = useAppContext()
	const userId = user?.id
	const clientId = company?.id

	// Check for existing localStorage photo and migrate it
	useEffect(() => {
		if (!userId || !clientId || hasCheckedLocalStorage) return

		const checkAndMigrateLocalStorage = async () => {
			setIsLoading(true)
			try {
				// Check for existing localStorage photo
				const localPhoto = retrieveStickyValue({
					storageName: "recruiterPhoto",
					sessionOnly: false,
					userId,
				})

				if (localPhoto) {
					// Try to upload to API
					try {
						await putUserPhoto({
							clientId,
							userId,
							fileB64: localPhoto,
						})

						// Upload successful, clean up localStorage
						deleteStickyValue({
							storageName: "recruiterPhoto",
							sessionOnly: false,
							userId,
						})

						setPhotoState(localPhoto)
					} catch (error) {
						console.warn("Failed to upload existing photo to API, keeping localStorage copy:", error)
						// Fallback to localStorage copy
						setPhotoState(localPhoto)
					}
				} else {
					// No localStorage photo, try to get from API
					try {
						const apiPhoto = await getUserPhoto({ clientId, userId })
						if (apiPhoto) {
							setPhotoState(apiPhoto)
						}
					} catch (error) {
						// No photo exists in API, which is fine
						console.log("No existing photo found in API:", error)
					}
				}
			} catch (error) {
				console.error("Error during photo migration check:", error)
			} finally {
				setIsLoading(false)
				setHasCheckedLocalStorage(true)
			}
		}

		checkAndMigrateLocalStorage()
	}, [userId, clientId, hasCheckedLocalStorage])

	// Setter function that uploads to API
	const setPhoto = useCallback(
		async (newPhoto: string | undefined) => {
			if (!userId || !clientId) {
				console.warn("Cannot set photo: missing userId or clientId")
				return
			}

			if (isLoading) {
				console.warn("Photo upload already in progress")
				return
			}

			setIsLoading(true)

			try {
				if (newPhoto) {
					// Upload new photo
					await putUserPhoto({
						clientId,
						userId,
						fileB64: newPhoto,
					})
				}
				// Note: We don't currently have a delete endpoint, so we can't remove photos from the API
				// For now, we'll just set undefined locally but the photo will remain in the backend

				setPhotoState(newPhoto)
			} catch (error) {
				console.error("Failed to upload photo to API:", error)
				throw error
			} finally {
				setIsLoading(false)
			}
		},
		[userId, clientId, isLoading]
	)

	// Getter function that fetches from API if needed
	const getPhoto = useCallback(async (): Promise<string | undefined> => {
		if (!userId || !clientId) {
			return undefined
		}

		try {
			const apiPhoto = await getUserPhoto({ clientId, userId })
			if (apiPhoto && apiPhoto !== photo) {
				setPhotoState(apiPhoto)
				return apiPhoto
			}
			return photo
		} catch (error) {
			console.debug("No photo found in API:", error)
			return photo
		}
	}, [userId, clientId, photo])

	return [photo, setPhoto, isLoading]
}
